import { BaseApiService, type ResponseData } from "./baseApiService";

// Specific interface for search API responses
interface SearchApiResponse extends ResponseData {
  user_context: UserContext;
}

export interface MatchingChunk {
  pageNumbers: number[];
  text: string;
  match_info: {
    score: number;
    nested_path: string;
  };
}

export interface SearchResult {
  id: string;
  policyCode: string;
  documentName: string;
  documentDescription: string;
  documentType: string;
  documentCategory: string;
  documentSubCategory: string;
  documentLanguage: string;
  agencyId: string;
  platform_org_id: string;
  audienceId: string;
  publishedDate: string;
  createdDate: string;
  modifiedDate: string;
  lastModifiedBy: string;
  documentVersion: string;
  _score: number;
  matching_chunks: MatchingChunk[];
}

export interface AssistantResponse {
  answer: string;
}

export interface UserContext {
  is_authenticated: boolean;
  access_mode: string;
  user_audiences: number[];
  total_accessible_policies?: number;
  org_name?: string;
  region_code?: string;
  locale?: string;
  timezone?: string;
  is_org_admin?: boolean;
  subscription_state?: string;
}

export interface SearchAggregations {
  reviewSchedule: Array<Record<string, number>>;
  audienceName: Array<Record<string, number>>;
  managerUserGroupName: Array<Record<string, number>>;
  documentType: Array<Record<string, number>>;
  yearCreated: Array<Record<string, number>>;
  documentCategory: Array<Record<string, number>>;
  documentStatus: Array<Record<string, number>>;
  documentSubCategory: Array<Record<string, number>>;
  publishedYear: Array<Record<string, number>>;
  agencyName: Array<Record<string, number>>;
}

export interface SearchData {
  documents: SearchResult[];
  aggregations: SearchAggregations;
  total: {
    value: number;
    relation: string;
  };
  took: number;
}

export interface SearchResponse {
  data: SearchData;
  user_context: UserContext;
}

class SearchService extends BaseApiService {
  // API 1: Search documents
  async searchDocuments(
    query: string,
    options?: { signal?: AbortSignal }
  ): Promise<SearchResponse> {
    if (!query || query.trim() === "") {
      throw new Error("Search query is required");
    }

    const searchParams = new URLSearchParams({
      searchText: query.trim(),
    });

    // Use static method that properly handles smart API routing
    const response = (await BaseApiService.get({
      url: `/search-results?${searchParams.toString()}`,
      responseDefaults: {},
      signal: options?.signal,
    })) as SearchApiResponse;

    if (response.success && response.data) {
      return response as SearchResponse;
    } else {
      throw new Error(response.error || "Failed to search documents");
    }
  }

  // API 1b: Advanced search with multiple parameters
  async searchDocumentsAdvanced(
    searchParams: {
      searchText?: string;
      policyCode?: string;
      documentType?: string;
      documentCategory?: string;
      documentSubCategory?: string;
      policyLanguage?: string;
      publishedSince?: Date | null;
    },
    options?: { signal?: AbortSignal }
  ): Promise<SearchResponse> {
    const urlSearchParams = new URLSearchParams({
      searchText: searchParams.searchText || "", // Use provided searchText or empty string
    });

    // Add non-empty parameters to query string
    if (searchParams.policyCode && searchParams.policyCode.trim()) {
      urlSearchParams.append("policyCode", searchParams.policyCode.trim());
    }

    if (searchParams.documentType && searchParams.documentType.trim()) {
      urlSearchParams.append("documentType", searchParams.documentType.trim());
    }

    if (searchParams.documentCategory && searchParams.documentCategory.trim()) {
      urlSearchParams.append(
        "documentCategory",
        searchParams.documentCategory.trim()
      );
    }

    if (
      searchParams.documentSubCategory &&
      searchParams.documentSubCategory.trim()
    ) {
      urlSearchParams.append(
        "documentSubCategory",
        searchParams.documentSubCategory.trim()
      );
    }

    if (searchParams.policyLanguage && searchParams.policyLanguage.trim()) {
      urlSearchParams.append(
        "documentLanguage",
        searchParams.policyLanguage.trim()
      );
    }

    if (searchParams.publishedSince) {
      const d = searchParams.publishedSince;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyymmdd = `${yyyy}-${mm}-${dd}`;
      urlSearchParams.append("publishedSinceDate", yyyymmdd);
    }

    console.log(
      "🌐 Advanced Search API URL:",
      `/search-results?${urlSearchParams.toString()}`
    );

    // Use static method that properly handles smart API routing
    const response = (await BaseApiService.get({
      url: `/search-results?${urlSearchParams.toString()}`,
      responseDefaults: {},
      signal: options?.signal,
    })) as SearchApiResponse;

    if (response.success && response.data) {
      return response as SearchResponse;
    } else {
      throw new Error(response.error || "Failed to perform advanced search");
    }
  }
}

// Export singleton instance
export const searchService = new SearchService();
